import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { Card, Row, Col, Statistic, Tag, Button, Tooltip, Spin, Empty } from 'antd';
import { ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { fetchCircuitBreakers } from '../../api/health';
import { CircuitBreaker } from '../../types/circuitBreaker';
import { useNavigate } from 'react-router-dom';
import { startTransition } from 'react';

// Create a wrapper component to handle the async data fetching
const CircuitBreakerStatusPanelContent: React.FC<{
  onRefresh: () => void;
  loading: boolean;
  error: string | null;
  circuitBreakers: CircuitBreaker[];
}> = ({ onRefresh, loading, error, circuitBreakers }) => {
  const { t: healthT } = useTranslationWithNamespace('health');
  const { t: commonT } = useTranslationWithNamespace('common');
  const navigate = useNavigate();

  // Get state tag color
  const getStateTagColor = (state: string) => {
    switch (state) {
      case 'CLOSED':
        return 'green';
      case 'OPEN':
        return 'red';
      case 'HALF_OPEN':
        return 'orange';
      default:
        return 'default';
    }
  };

  // Count circuit breakers by state
  const countByState = {
    CLOSED: circuitBreakers.filter(cb => cb.state === 'CLOSED').length,
    OPEN: circuitBreakers.filter(cb => cb.state === 'OPEN').length,
    HALF_OPEN: circuitBreakers.filter(cb => cb.state === 'HALF_OPEN').length
  };

  // Get open circuit breakers
  const openCircuitBreakers = circuitBreakers.filter(cb => cb.state === 'OPEN' || cb.state === 'HALF_OPEN');

  return (
    <>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin />
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Empty description={error} />
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Statistic
                title={healthT('circuitBreakers.closedCount', 'Closed')}
                value={countByState.CLOSED}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={healthT('circuitBreakers.openCount', 'Open')}
                value={countByState.OPEN}
                valueStyle={{ color: '#f5222d' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={healthT('circuitBreakers.halfOpenCount', 'Half-Open')}
                value={countByState.HALF_OPEN}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Col>
          </Row>

          {openCircuitBreakers.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4>{healthT('circuitBreakers.openCircuitBreakers', 'Open Circuit Breakers:')}</h4>
              <div>
                {openCircuitBreakers.map(cb => (
                  <Tooltip
                    key={cb.name}
                    title={`${cb.name} - ${cb.metrics?.operationType || 'Unknown'}`}
                  >
                    <Tag
                      color={getStateTagColor(cb.state)}
                      style={{ margin: '4px' }}
                    >
                      {cb.name.split(':')[1] || cb.name}
                    </Tag>
                  </Tooltip>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<SettingOutlined />}
              onClick={() => navigate('/circuit-breakers')}
            >
              {healthT('circuitBreakers.manage', 'Manage Circuit Breakers')}
            </Button>
          </div>
        </>
      )}
    </>
  );
};

// Main component that handles data fetching
const CircuitBreakerStatusPanel: React.FC = () => {
  const { t: healthT } = useTranslationWithNamespace('health');
  const { t: commonT } = useTranslationWithNamespace('common');

  const [circuitBreakers, setCircuitBreakers] = useState<CircuitBreaker[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch circuit breakers
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use startTransition to prevent React from suspending during synchronous updates
      startTransition(() => {
        fetchCircuitBreakers()
          .then(data => {
            setCircuitBreakers(data.circuitBreakers || []);
            setLoading(false);
          })
          .catch(err => {
            console.error('Error fetching circuit breakers:', err);
            setError(healthT('circuitBreakers.fetchError', 'Failed to fetch circuit breakers'));
            setLoading(false);
          });
      });
    } catch (err) {
      console.error('Error in fetchData:', err);
      setError(healthT('circuitBreakers.fetchError', 'Failed to fetch circuit breakers'));
      setLoading(false);
    }
  }, [healthT]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Card
      title={healthT('circuitBreakers.statusTitle', 'Circuit Breaker Status')}
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchData}
          loading={loading}
          size="small"
        >
          {commonT('actions.refresh')}
        </Button>
      }
    >
      <Suspense fallback={<div style={{ textAlign: 'center', padding: '20px' }}><Spin /></div>}>
        <CircuitBreakerStatusPanelContent
          onRefresh={fetchData}
          loading={loading}
          error={error}
          circuitBreakers={circuitBreakers}
        />
      </Suspense>
    </Card>
  );
};

export default CircuitBreakerStatusPanel;
